export const PRICING_WHATSAPP_NUMBER = '9873009199';

export const PRICING_YEARLY_DISCOUNT_PERCENT = 10;

export const PRICING_GST_PERCENT = 18;

/** @typedef {'monthly' | 'yearly'} PricingBillingCycle */

export const PRICING_BILLING_TABS = [
  { id: 'monthly', label: 'Monthly' },
  { id: 'yearly', label: 'Yearly', badge: `Save ${PRICING_YEARLY_DISCOUNT_PERCENT}%` },
];

const roundInr = (value) => Math.round(Number(value) || 0);

const yearlyPriceFromMonthly = (monthlyPriceInr) =>
  roundInr(monthlyPriceInr * 12 * (1 - PRICING_YEARLY_DISCOUNT_PERCENT / 100));

const BASE_PLANS = [
  {
    id: 'basic',
    name: 'Basic',
    monthlyPriceInr: 1999,
    imageCredits: 100,
    popular: false,
    gradient: 'from-blue-500 to-cyan-500',
    bgGradient: 'from-blue-50 to-cyan-50',
    icon: 'layers',
    features: [
      '100 image credits',
      'Unlimited fabric catalogue',
      'AI concept generation',
      'Studio access',
    ],
    exclusions: [],
  },
  {
    id: 'advanced',
    name: 'Advanced Generation',
    monthlyPriceInr: 4999,
    imageCredits: 500,
    popular: true,
    gradient: 'from-[#5424e4] to-[#17bcf7]',
    bgGradient: 'from-[#eef0ff] to-[#d9f6fe]',
    icon: 'sparkles',
    features: [
      '500 image credits',
      'Unlimited fabric catalogue',
      'AI concept generation',
      'Studio access',
    ],
    exclusions: [],
  },
];

function buildCyclePlan(basePlan, billingCycle) {
  const isYearly = billingCycle === 'yearly';
  const monthlyPrice = basePlan.monthlyPriceInr;
  const priceInr = isYearly ? yearlyPriceFromMonthly(monthlyPrice) : monthlyPrice;

  return {
    id: isYearly ? `${basePlan.id}_yearly` : basePlan.id,
    basePlanId: basePlan.id,
    name: basePlan.name,
    priceInr,
    ...(isYearly
      ? {
          originalPriceInr: monthlyPrice * 12,
          billingCycle: 'yearly',
          periodLabel: 'per year',
        }
      : {
          billingCycle: 'monthly',
          periodLabel: 'per month',
        }),
    popular: basePlan.popular,
    gradient: basePlan.gradient,
    bgGradient: basePlan.bgGradient,
    icon: basePlan.icon,
    imageCredits: basePlan.imageCredits,
    features: [
      isYearly
        ? `${basePlan.imageCredits} image credits per month`
        : `${basePlan.imageCredits} image credits`,
      ...basePlan.features.slice(1),
      isYearly ? '12 months access' : '1 month access',
    ],
    exclusions: basePlan.exclusions,
  };
}

export const PRICING_PLANS = [
  ...BASE_PLANS.flatMap((plan) => [
    buildCyclePlan(plan, 'monthly'),
    buildCyclePlan(plan, 'yearly'),
  ]),
  {
    id: 'try_on_screen',
    name: 'Try On Screen',
    priceInr: 79999,
    alwaysVisible: true,
    billingCycle: 'monthly',
    periodLabel: 'one-time',
    popular: false,
    gradient: 'from-[#5424e4] to-[#8b5cf6]',
    bgGradient: 'from-[#eef0ff] to-[#f3e8ff]',
    icon: 'crown',
    image: '/assets/try-on-screen.png?v=20260818',
    features: [
      'Advanced Generation plan FREE for 3 months',
      '1,500 image credits included (500/month × 3)',
      'Interactive virtual Try-On screen',
      'In-store customer experience',
      'Dedicated setup & support',
    ],
    exclusions: [],
  },
];

export function formatPlanFeature(feature) {
  if (typeof feature === 'string') return feature;
  return feature?.text || feature?.label || '';
}

export function getPlanFeatureStruckText(feature) {
  if (typeof feature !== 'object' || !feature) return null;
  if (feature.struckText) return feature.struckText;
  if (feature.strikethrough || feature.struck) return feature.text || feature.label || null;
  return null;
}

export function isPlanFeatureStruck(feature) {
  return typeof feature === 'object' && Boolean(feature?.strikethrough || feature?.struck);
}

export function isPlanFeatureStruckOnly(feature) {
  return isPlanFeatureStruck(feature) && !feature?.struckText;
}

export function resolvePlanPrice(plan) {
  return Number(plan?.priceInr || 0);
}

export function resolvePlanOriginalPrice(plan) {
  const original = Number(plan?.originalPriceInr || 0);
  const current = resolvePlanPrice(plan);
  return original > current ? original : null;
}

export function resolvePlanPeriodLabel(plan) {
  return plan?.periodLabel || '';
}

export function resolvePlanGstInr(baseAmountInr) {
  return (Number(baseAmountInr) || 0) * (PRICING_GST_PERCENT / 100);
}

export function resolvePlanTotalWithGstInr(baseAmountInr) {
  const base = Number(baseAmountInr) || 0;
  return base + resolvePlanGstInr(base);
}

export function formatInr(amountInr) {
  return (Number(amountInr) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

export function resolvePlanBillingCycle(plan) {
  return plan?.billingCycle || 'monthly';
}

export function getVisiblePricingPlans(isAdmin = false, billingCycle = 'monthly') {
  return PRICING_PLANS.filter((plan) => {
    if (plan.adminOnly) return isAdmin;
    if (plan.alwaysVisible) return true;
    return resolvePlanBillingCycle(plan) === billingCycle;
  });
}

export function buildPricingWhatsAppMessage(plan) {
  const price = resolvePlanPrice(plan);
  const periodLabel = resolvePlanPeriodLabel(plan);
  const totalWithGst = resolvePlanTotalWithGstInr(price);
  const priceLine = `₹${formatInr(price)} + ${PRICING_GST_PERCENT}% GST = ₹${formatInr(totalWithGst)} ${periodLabel}`.trim();
  const featureLines = (plan.features || []).map((feature) => formatPlanFeature(feature)).filter(Boolean);

  return `Hi! I'm interested in purchasing the *${plan.name}* plan.\n\n📦 Plan Details:\n• Price: ${priceLine}\n• ${featureLines.join('\n• ')}\n\nPlease help me complete the purchase. Thank you!`;
}
